import { getOptimizedImage } from "../components/images/resolveImage";
import { buildGradientOrSolidBackground } from '../config';
import { elakayAutoText } from '../includes/pageInside/dataElakay';

export const CARD_GLOBAL_STYLE_DEFAULTS = Object.freeze({
  enabled: false,
  mergeWithParent: false,
  borderEnabled: true,
  shadowEnabled: true,
  radiusEnabled: true,
  backgroundColor: '#ffffff',
  borderColor: 'rgba(0, 0, 0, 0.08)',
  shadowColor: 'rgba(0, 115, 153, 0.35)',
  textColor: '#2d3748',
  padding: '',
  margin: '',
});

export const LAYOUT_BACKGROUND_DEFAULTS = Object.freeze({
  enabled: false,
  backgroundColor: '#f5f7fa',
  backgroundImage: '',
  imageOpacity: 1,
  gradientEnabled: false,
  gradientColor1: '#f5f7fa',
  gradientColor2: '#c3cfe2',
  gradientDirection: '135deg',
  overlayOpacity: 0,
  overlayGradientOpacity: 1,
  backgroundRepeat: 'no-repeat',
  backgroundAttachment: 'scroll',
  backgroundSize: 'cover',
  backgroundSizeCustom: '',
  backgroundPosition: 'center',
});

// Fond + box-model de la zone de contenu centrale (entre Header et Footer).
// Fusion de "Layout" + "Page Body Background". S'applique uniquement sur
// .elakay-page-body-area dans Layout.js — jamais sur Header/Footer/body/root.
export const LAYOUT_BODY_BACKGROUND_DEFAULTS = Object.freeze({
  ...LAYOUT_BACKGROUND_DEFAULTS,
  backgroundColorSecondary: '',
  padding: '',
  margin: '',
  borderRadius: '',
  borderEnabled: false,
  borderColor: 'rgba(0, 0, 0, 0.08)',
  borderWidth: '1px',
  boxShadowEnabled: false,
  boxShadowColor: 'rgba(15, 23, 42, 0.25)',
  minHeight: '',
});

const CSS_COLOR_PATTERN = /^(?:#[0-9a-f]{3,8}|rgba?\(\s*[\d.%]+\s*,\s*[\d.%]+\s*,\s*[\d.%]+(?:\s*,\s*[\d.%]+)?\s*\)|hsla?\(\s*[-\d.]+(?:deg)?\s*,\s*[\d.]+%\s*,\s*[\d.]+%(?:\s*,\s*[\d.%]+)?\s*\)|[a-z]{3,30}|transparent)$/i;
const CSS_SPACE_PATTERN = /^(?:0|-?\d+(?:\.\d+)?(?:px|rem|em|%|vh|vw))(?:\s+(?:0|-?\d+(?:\.\d+)?(?:px|rem|em|%|vh|vw))){0,3}$/i;
const CSS_SIZE_PATTERN = /^(?:auto|cover|contain|\d+(?:\.\d+)?(?:px|rem|em|%|vh|vw))(?:\s+(?:auto|\d+(?:\.\d+)?(?:px|rem|em|%|vh|vw)))?$/i;
const CSS_DIRECTION_PATTERN = /^(?:to\s+(?:top|bottom|left|right)(?:\s+(?:top|bottom|left|right))?|-?\d+(?:\.\d+)?(?:deg|rad|turn))$/i;

export const isSafeCssColor = (value) => !value || CSS_COLOR_PATTERN.test(String(value).trim());
export const isSafeCssSpacing = (value) => !value || CSS_SPACE_PATTERN.test(String(value).trim());
export const isSafeCssSize = (value) => !value || CSS_SIZE_PATTERN.test(String(value).trim());
export const isSafeGradientDirection = (value) => CSS_DIRECTION_PATTERN.test(String(value || '').trim());

export const isSafeImageUrl = (value) => {
  const imageUrl = String(value || '').trim();
  if (!imageUrl) return true;
  if (/^(?:javascript|data|vbscript):/i.test(imageUrl) || [...imageUrl].some((character) => character.charCodeAt(0) < 32)) return false;
  if (imageUrl.startsWith('/')) return true;
  try {
    return ['http:', 'https:'].includes(new URL(imageUrl).protocol);
  } catch {
    return false;
  }
};

const BORDER_WIDTH_PATTERN = /^\d+(?:\.\d+)?(?:px|rem|em)$/i;

export const validateAppearanceSettings = (settings = {}) => {
  const card = settings.cardGlobalStyle || CARD_GLOBAL_STYLE_DEFAULTS;
  const background = settings.layoutBackground || LAYOUT_BACKGROUND_DEFAULTS;
  const bodyArea = settings.layoutBodyBackground || LAYOUT_BODY_BACKGROUND_DEFAULTS;
  const invalidColors = [
    card.backgroundColor,
    card.borderColor,
    card.shadowColor,
    card.textColor,
    background.backgroundColor,
    background.gradientColor1,
    background.gradientColor2,
    bodyArea.backgroundColor,
    bodyArea.backgroundColorSecondary,
    bodyArea.gradientColor1,
    bodyArea.gradientColor2,
    bodyArea.borderColor,
    bodyArea.boxShadowColor,
  ].some((value) => !isSafeCssColor(value));

  if (invalidColors) return elakayAutoText("h_appearance_invalid_color", "A color contains a CSS value that isn't allowed.");
  if (!isSafeCssSpacing(card.padding) || !isSafeCssSpacing(card.margin)) {
    return elakayAutoText("h_appearance_invalid_padding_margin", "Invalid padding/margin. Use 1 to 4 values with px, rem, em, %, vh or vw.");
  }
  if (!isSafeImageUrl(background.backgroundImage)) return elakayAutoText("h_appearance_invalid_image_url", "Invalid image URL or protocol not allowed.");
  if (!isSafeGradientDirection(background.gradientDirection)) return elakayAutoText("h_appearance_invalid_gradient_direction", "Invalid gradient direction.");
  if (background.backgroundSize === 'custom' && !isSafeCssSize(background.backgroundSizeCustom)) {
    return elakayAutoText("h_appearance_invalid_custom_bg_size", "Invalid custom background size.");
  }
  if (!isSafeImageUrl(bodyArea.backgroundImage)) return elakayAutoText("h_appearance_invalid_image_url_body", "Invalid image URL or protocol not allowed (central area).");
  if (!isSafeGradientDirection(bodyArea.gradientDirection)) return elakayAutoText("h_appearance_invalid_gradient_direction_body", "Invalid gradient direction (central area).");
  if (bodyArea.backgroundSize === 'custom' && !isSafeCssSize(bodyArea.backgroundSizeCustom)) {
    return elakayAutoText("h_appearance_invalid_custom_bg_size_body", "Invalid custom background size (central area).");
  }
  if (!isSafeCssSpacing(bodyArea.padding) || !isSafeCssSpacing(bodyArea.margin)
    || !isSafeCssSpacing(bodyArea.borderRadius) || !isSafeCssSpacing(bodyArea.minHeight)) {
    return elakayAutoText("h_appearance_invalid_spacing_body", "Invalid padding/margin/borderRadius/minHeight (central area).");
  }
  if (!BORDER_WIDTH_PATTERN.test(String(bodyArea.borderWidth || '1px').trim())) {
    return elakayAutoText("h_appearance_invalid_border_width_body", "Invalid border width (central area).");
  }

  const titleStyle = settings.defaultTitleStyle || {};
  const invalidTitleColors = [
    titleStyle.textColor,
    titleStyle.hoverColor,
    titleStyle.activeColor,
    titleStyle.disabledColor,
    titleStyle.textGradientColor1,
    titleStyle.textGradientColor2,
    titleStyle.backgroundColor,
    titleStyle.backgroundGradientColor1,
    titleStyle.backgroundGradientColor2,
    titleStyle.borderColor,
    titleStyle.outlineColor,
    titleStyle.boxShadowColor,
  ].some((value) => !isSafeCssColor(value));
  if (invalidTitleColors) return elakayAutoText("h_appearance_invalid_title_color", "A \"Default Title Style\" color contains a CSS value that isn't allowed.");
  if (!isSafeGradientDirection(titleStyle.gradientDirection)) return elakayAutoText("h_appearance_invalid_gradient_direction_title", "Invalid gradient direction (Default Title Style).");
  if (!isSafeCssSpacing(titleStyle.marginTop) || !isSafeCssSpacing(titleStyle.marginBottom)
    || !isSafeCssSpacing(titleStyle.paddingTop) || !isSafeCssSpacing(titleStyle.paddingBottom)
    || !isSafeCssSpacing(titleStyle.borderRadius)) {
    return elakayAutoText("h_appearance_invalid_spacing_title", "Invalid margin/padding/borderRadius (Default Title Style).");
  }
  if (!BORDER_WIDTH_PATTERN.test(String(titleStyle.borderWidth || '1px').trim())
    || !BORDER_WIDTH_PATTERN.test(String(titleStyle.outlineWidth || '1px').trim())) {
    return elakayAutoText("h_appearance_invalid_border_outline_width_title", "Invalid border/outline width (Default Title Style).");
  }

  const cardDisplay = settings.cardDisplaySettings?.landscape || {};
  if (!isSafeCssColor(cardDisplay.descriptionColor)) {
    return elakayAutoText("h_appearance_invalid_description_color", "Invalid description color (Card Display Mode).");
  }
  if (!isSafeCssSize(cardDisplay.descriptionFontSize)) {
    return elakayAutoText("h_appearance_invalid_description_font_size", "Invalid description font size (Card Display Mode).");
  }
  if (cardDisplay.descriptionLineHeight && !/^\d+(?:\.\d+)?$/.test(String(cardDisplay.descriptionLineHeight).trim())) {
    return elakayAutoText("h_appearance_invalid_description_line_height", "Invalid description line height (Card Display Mode).");
  }
  return null;
};

const clampOpacity = (value, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(1, Math.max(0, number)) : fallback;
};

export const getCardGlobalPresentation = (rawSettings, isHovered = false, fallbackShadowColor) => {
  const settings = { ...CARD_GLOBAL_STYLE_DEFAULTS, ...(rawSettings || {}) };
  if (!settings.enabled) return { className: '', style: {}, variables: {} };

  const mergeMode = settings.mergeWithParent === true;
  const backgroundColor = isSafeCssColor(settings.backgroundColor) ? settings.backgroundColor : CARD_GLOBAL_STYLE_DEFAULTS.backgroundColor;
  const borderColor = isSafeCssColor(settings.borderColor) ? settings.borderColor : CARD_GLOBAL_STYLE_DEFAULTS.borderColor;
  const textColor = isSafeCssColor(settings.textColor) ? settings.textColor : CARD_GLOBAL_STYLE_DEFAULTS.textColor;
  const configuredShadow = isSafeCssColor(settings.shadowColor) ? settings.shadowColor : null;
  const shadowColor = configuredShadow || fallbackShadowColor || CARD_GLOBAL_STYLE_DEFAULTS.shadowColor;
  const style = {
    border: mergeMode || !settings.borderEnabled ? 'none' : `1px solid ${borderColor}`,
    boxShadow: mergeMode || !settings.shadowEnabled
      ? 'none'
      : `0 ${isHovered ? '12px 32px' : '4px 12px'} ${shadowColor}`,
    borderRadius: mergeMode || !settings.radiusEnabled ? 0 : '12px',
    background: mergeMode ? 'transparent' : backgroundColor,
    color: textColor,
  };
  if (settings.padding && isSafeCssSpacing(settings.padding)) style.padding = settings.padding;
  if (settings.margin && isSafeCssSpacing(settings.margin)) style.margin = settings.margin;

  return {
    className: `card-elakay-global global-style-enabled${mergeMode ? ' merge-mode' : ''}`,
    style,
    variables: {
      '--elakay-card-background': mergeMode ? 'transparent' : backgroundColor,
      '--elakay-card-text-color': textColor,
      '--elakay-card-radius': mergeMode || !settings.radiusEnabled ? '0px' : '12px',
    },
  };
};

export const buildLayoutBackgroundLayers = (rawSettings = {}) => {
  const settings = { ...LAYOUT_BACKGROUND_DEFAULTS, ...(rawSettings || {}) };
  if (!settings.enabled) return null;
  const safeImage = isSafeImageUrl(settings.backgroundImage) ? settings.backgroundImage : '';
  const safeGradient = settings.gradientEnabled
    && isSafeGradientDirection(settings.gradientDirection)
    && isSafeCssColor(settings.gradientColor1)
    && isSafeCssColor(settings.gradientColor2);
  const effectiveSize = settings.backgroundSize === 'custom'
    ? (isSafeCssSize(settings.backgroundSizeCustom) ? (settings.backgroundSizeCustom || 'auto') : 'auto')
    : (['cover', 'contain', 'auto', '100% 100%'].includes(settings.backgroundSize) ? settings.backgroundSize : 'cover');
  const repeat = ['no-repeat', 'repeat', 'repeat-x', 'repeat-y'].includes(settings.backgroundRepeat) ? settings.backgroundRepeat : 'no-repeat';
  const attachment = ['scroll', 'fixed', 'local'].includes(settings.backgroundAttachment) ? settings.backgroundAttachment : 'scroll';
  const position = ['center', 'top', 'bottom', 'left', 'right'].includes(settings.backgroundPosition) ? settings.backgroundPosition : 'center';

  return {
    rootStyle: { backgroundColor: isSafeCssColor(settings.backgroundColor) ? (settings.backgroundColor || 'transparent') : 'transparent' },
    imageStyle: safeImage ? {
      backgroundImage: `url("${String(getOptimizedImage(safeImage, { preset: "banner" }) || safeImage).replace(/["\\\n\r]/g, '')}")`,
      backgroundRepeat: repeat,
      backgroundAttachment: attachment,
      backgroundSize: effectiveSize,
      backgroundPosition: position,
      opacity: clampOpacity(settings.imageOpacity, 1),
    } : null,
    gradientStyle: safeGradient ? {
      backgroundImage: `linear-gradient(${settings.gradientDirection}, ${settings.gradientColor1}, ${settings.gradientColor2})`,
      opacity: clampOpacity(settings.overlayGradientOpacity, 1),
    } : null,
    overlayStyle: clampOpacity(settings.overlayOpacity, 0) > 0 ? {
      backgroundColor: '#000000',
      opacity: clampOpacity(settings.overlayOpacity, 0),
    } : null,
  };
};

// Migration lecture : si layoutBodyBackground n'a pas encore été sauvegardé,
// reconstruit l'équivalent depuis les anciens champs layoutBackground /
// layout.backgroundColor(Secondary). Les anciens champs restent en base tels
// quels (jamais supprimés) — voir AnimationSettings.jsx et Layout.js.
export const resolveLayoutBodyBackground = (allSettings = {}) => {
  const explicit = allSettings?.layoutBodyBackground;
  if (explicit && typeof explicit === 'object' && Object.keys(explicit).length > 0) {
    return { ...LAYOUT_BODY_BACKGROUND_DEFAULTS, ...explicit };
  }

  const legacyBackground = allSettings?.layoutBackground || {};
  const legacyLayout = allSettings?.layout || {};
  return {
    ...LAYOUT_BODY_BACKGROUND_DEFAULTS,
    ...legacyBackground,
    backgroundColor: legacyBackground.backgroundColor
      ?? legacyLayout.backgroundColor
      ?? LAYOUT_BODY_BACKGROUND_DEFAULTS.backgroundColor,
    backgroundColorSecondary: legacyLayout.backgroundColorSecondary
      ?? LAYOUT_BODY_BACKGROUND_DEFAULTS.backgroundColorSecondary,
  };
};

// Fond (calques) + box-model du conteneur central (.elakay-page-body-area).
// containerStyle est calculé indépendamment du toggle "enabled" du fond
// riche : padding/margin/border/shadow/minHeight restent des réglages
// indépendants (cf. décision de ne pas fusionner avec Card Global Style).
export const buildLayoutBodyBackgroundPresentation = (rawSettings = {}) => {
  const settings = { ...LAYOUT_BODY_BACKGROUND_DEFAULTS, ...(rawSettings || {}) };

  const containerStyle = {};
  if (settings.padding && isSafeCssSpacing(settings.padding)) containerStyle.padding = settings.padding;
  if (settings.margin && isSafeCssSpacing(settings.margin)) containerStyle.margin = settings.margin;
  if (settings.minHeight && isSafeCssSpacing(settings.minHeight)) containerStyle.minHeight = settings.minHeight;
  if (settings.borderRadius && isSafeCssSpacing(settings.borderRadius)) containerStyle.borderRadius = settings.borderRadius;
  if (settings.borderEnabled) {
    const borderColor = isSafeCssColor(settings.borderColor) ? settings.borderColor : LAYOUT_BODY_BACKGROUND_DEFAULTS.borderColor;
    const borderWidth = BORDER_WIDTH_PATTERN.test(String(settings.borderWidth || '').trim())
      ? settings.borderWidth
      : LAYOUT_BODY_BACKGROUND_DEFAULTS.borderWidth;
    containerStyle.border = `${borderWidth} solid ${borderColor}`;
  }
  if (settings.boxShadowEnabled) {
    const shadowColor = isSafeCssColor(settings.boxShadowColor) ? settings.boxShadowColor : LAYOUT_BODY_BACKGROUND_DEFAULTS.boxShadowColor;
    containerStyle.boxShadow = `0 8px 30px ${shadowColor}`;
  }

  const layers = buildLayoutBackgroundLayers(settings);
  let rootStyle = layers?.rootStyle || null;
  if (!layers && !settings.gradientEnabled && settings.backgroundColorSecondary) {
    rootStyle = buildGradientOrSolidBackground(settings.backgroundColor, settings.backgroundColorSecondary, 135) || null;
  }

  return {
    rootStyle,
    imageStyle: layers?.imageStyle || null,
    gradientStyle: layers?.gradientStyle || null,
    overlayStyle: layers?.overlayStyle || null,
    containerStyle,
  };
};
